// Public Credit Builder hub — /credit-builder
// SEO-optimized landing page that lists all 13 provinces and explains the
// rent-reporting program. Each province card links to /credit-builder/:slug.
import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import {
  TrendingUp, ShieldCheck, ArrowRight, MapPin, CheckCircle,
  Loader2, Sparkles, Building2, Mail, X,
} from "lucide-react";
import PublicHeader from "@/components/layout/PublicHeader";
import { useBotShield, BotHoneypot } from "@/lib/botShield";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

// ─────────────────────────── Enrollment dialog ──────────────────────────
// Public tenant self-enroll modal. Submits to POST /credit-builder/enroll
// (no admin token required). Shows success state with what-happens-next.
function EnrollDialog({ open, onClose, provinces, defaultProvince }) {
  const [form, setForm] = useState({
    tenant_name: "",
    tenant_email: "",
    province: defaultProvince || "",
    monthly_rent: "",
    lease_start: "",
    property_address: "",
    landlord_email: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState("");
  const shield = useBotShield();

  useEffect(() => {
    if (open) {
      setForm((f) => ({ ...f, province: defaultProvince || f.province || (provinces[0]?.slug || "") }));
      setDone(null);
      setError("");
    }
  }, [open, defaultProvince, provinces]);

  if (!open) return null;

  async function submit(e) {
    e?.preventDefault?.();
    if (!form.tenant_name.trim() || !form.tenant_email.trim() || !form.province || !form.monthly_rent) {
      setError("Please fill in name, email, province and monthly rent.");
      return;
    }
    setBusy(true); setError("");
    try {
      const utm = new URLSearchParams(window.location.search);
      const r = await fetch(`${API}/api/credit-builder/enroll`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          monthly_rent: Number(form.monthly_rent),
          landlord_email: form.landlord_email.trim() || null,
          utm_source: utm.get("utm_source"),
          utm_medium: utm.get("utm_medium"),
          utm_campaign: utm.get("utm_campaign"),
          ...shield.values(),
        }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || `HTTP ${r.status}`);
      }
      setDone(await r.json());
      try {
        const { fireLeadFormSubmit } = await import("@/lib/google-tag");
        fireLeadFormSubmit({ source: "credit_builder_enroll", email: form.tenant_email, valueCad: 25 });
      } catch { /* never block UX */ }
    } catch (e) {
      setError(e.message || "Enrollment failed — please try again.");
    } finally { setBusy(false); }
  }

  return (
    <div role="dialog" aria-modal="true" data-testid="enroll-dialog" className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div>
            <div className="font-display text-lg font-extrabold text-slate-900">Enroll in Credit Builder</div>
            <div className="text-xs text-slate-500">Free for tenants · Cancel anytime</div>
          </div>
          <button onClick={onClose} data-testid="enroll-dialog-close" aria-label="Close" className="p-2 rounded-lg hover:bg-slate-100"><X className="w-4 h-4"/></button>
        </div>

        {done ? (
          <div className="p-6 text-center" data-testid="enroll-dialog-success">
            <div className="mx-auto w-14 h-14 rounded-full bg-emerald-600 text-white flex items-center justify-center mb-3">
              <CheckCircle className="w-7 h-7"/>
            </div>
            <div className="font-display text-xl font-extrabold text-slate-900">You&apos;re enrolled!</div>
            <p className="mt-1 text-sm text-slate-600">{done.note}</p>
            <div className="mt-5 rounded-xl bg-slate-50 border border-slate-200 p-4 text-left text-xs text-slate-700">
              <b className="text-slate-900">What happens next</b>
              <ol className="mt-2 space-y-1 list-decimal list-inside">
                <li>We verify your lease with your landlord (24–48h)</li>
                <li>Every on-time payment is reported to Equifax + TransUnion</li>
                <li>Most enrollees see a measurable score lift within 90 days</li>
              </ol>
            </div>
            <button onClick={onClose} data-testid="enroll-dialog-done" className="mt-5 w-full px-4 py-2.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-bold text-sm">Done</button>
          </div>
        ) : (
          <form onSubmit={submit} noValidate className="p-5 space-y-3" data-testid="enroll-dialog-form">
            <BotHoneypot {...shield.fieldProps} />
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Full name</label>
                <input data-testid="enroll-name" value={form.tenant_name} onChange={(e) => setForm({ ...form, tenant_name: e.target.value })} placeholder="Jane Smith" className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-lg outline-none focus:border-emerald-500"/>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Email</label>
                <input data-testid="enroll-email" type="email" value={form.tenant_email} onChange={(e) => setForm({ ...form, tenant_email: e.target.value })} placeholder="jane@email.ca" className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-lg outline-none focus:border-emerald-500"/>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Province</label>
                <select data-testid="enroll-province" value={form.province} onChange={(e) => setForm({ ...form, province: e.target.value })} className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-lg bg-white">
                  {provinces.map((p) => <option key={p.slug || p.code} value={p.slug || p.code}>{p.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Monthly rent (CA$)</label>
                <input data-testid="enroll-rent" type="number" min={1} max={20000} value={form.monthly_rent} onChange={(e) => setForm({ ...form, monthly_rent: e.target.value })} placeholder="2400" className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-lg outline-none focus:border-emerald-500"/>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Lease start (optional)</label>
                <input data-testid="enroll-lease-start" type="date" value={form.lease_start} onChange={(e) => setForm({ ...form, lease_start: e.target.value })} className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-lg outline-none focus:border-emerald-500"/>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Landlord email (optional)</label>
                <input data-testid="enroll-landlord-email" type="email" value={form.landlord_email} onChange={(e) => setForm({ ...form, landlord_email: e.target.value })} placeholder="landlord@email.ca" className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-lg outline-none focus:border-emerald-500"/>
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Property address (optional)</label>
              <input data-testid="enroll-address" value={form.property_address} onChange={(e) => setForm({ ...form, property_address: e.target.value })} placeholder="123 Main St, Toronto ON" className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-lg outline-none focus:border-emerald-500"/>
            </div>
            {error && <div className="text-xs text-rose-600" data-testid="enroll-error">{error}</div>}
            <button type="submit" disabled={busy} data-testid="enroll-submit" className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-700 hover:bg-emerald-800 text-white font-bold text-sm disabled:opacity-50">
              {busy ? <><Loader2 className="w-4 h-4 animate-spin"/> Enrolling…</> : <>Enroll for free <ArrowRight className="w-4 h-4"/></>}
            </button>
            <p className="text-[10px] text-slate-500 text-center">Cancel any time · We never share your info with third parties beyond the credit bureaus</p>
          </form>
        )}
      </div>
    </div>
  );
}

// JSON-LD helper — injects structured data into <head> so search
// engines can render rich results for this page.
function JsonLd({ data }) {
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
    />
  );
}

// Native <title> + meta helper — we set these directly to keep the
// dependency list minimal (no react-helmet).
function useDocumentMeta({ title, description, canonical }) {
  useEffect(() => {
    if (title) document.title = title;
    if (description) {
      let m = document.querySelector('meta[name="description"]');
      if (!m) {
        m = document.createElement("meta");
        m.setAttribute("name", "description");
        document.head.appendChild(m);
      }
      m.setAttribute("content", description);
    }
    if (canonical) {
      let link = document.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        document.head.appendChild(link);
      }
      link.setAttribute("href", canonical);
    }
  }, [title, description, canonical]);
}

// ────────────────────────────────────────────────────────────────────────
// /credit-builder  — Hub page
// ────────────────────────────────────────────────────────────────────────
export default function CreditBuilderHub() {
  const [provinces, setProvinces] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [enrollOpen, setEnrollOpen] = useState(false);

  useDocumentMeta({
    title: "Build credit by paying rent — Credit Builder | Central Rentals Canada",
    description: "Report your on-time rent payments to Equifax + TransUnion Canada and grow your credit score. Free for tenants. Available in all 13 Canadian provinces and territories.",
    canonical: "https://centralrentals.ca/credit-builder",
  });

  useEffect(() => {
    fetch(`/api/credit-builder/manifest`)
      .then((r) => r.json())
      .then((d) => setProvinces(d.provinces || []))
      .catch(() => setProvinces([]))
      .finally(() => setLoading(false));
  }, []);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Central Rentals Credit Builder",
    serviceType: "Rent reporting & credit building",
    areaServed: { "@type": "Country", name: "Canada" },
    provider: {
      "@type": "Organization",
      name: "Central Rentals Canada",
      url: "https://centralrentals.ca",
    },
    description: "Report on-time rent payments to Canadian credit bureaus so tenants build credit history every month.",
    audience: { "@type": "Audience", audienceType: "Canadian renters" },
    offers: { "@type": "Offer", price: "0", priceCurrency: "CAD" },
  };

  // Detect existing auth session so logged-in tenants don't feel "logged
  // out" when they land on this public page from their dashboard. Token
  // stays in localStorage — we just surface a friendlier back-link.
  const isAuthed = typeof window !== "undefined" && !!(window.localStorage?.getItem?.("crc_session_token"));

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="credit-builder-hub">
      <JsonLd data={jsonLd}/>
      <PublicHeader/>
      {isAuthed && (
        <div className="bg-emerald-50 border-b border-emerald-200" data-testid="cb-authed-banner">
          <div className="max-w-6xl mx-auto px-5 md:px-8 py-2 text-xs text-emerald-900 flex items-center justify-between gap-3">
            <span>You're still signed in.</span>
            <Link to="/dashboard" data-testid="cb-back-to-dashboard" className="font-bold underline">← Back to your dashboard</Link>
          </div>
        </div>
      )}

      {/* Hero */}
      <section className="bg-gradient-to-br from-emerald-50 via-white to-sky-50 border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-16 md:py-24">
          <div className="text-[11px] tracking-[0.22em] uppercase font-bold text-emerald-700 inline-flex items-center gap-1.5">
            <Sparkles className="w-3 h-3"/> Free for tenants
          </div>
          <h1 className="font-display mt-3 text-3xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-[1.05] max-w-3xl">
            The rent you already pay should <span className="text-emerald-700">build your credit</span>.
          </h1>
          <p className="mt-5 text-base md:text-lg text-slate-700 max-w-2xl leading-relaxed">
            Central Rentals Credit Builder reports your on-time rent payments to
            <strong> Equifax</strong> and <strong>TransUnion Canada</strong>, so every
            month you pay rent turns into a credit-history line on your report.
            Available in all 13 Canadian provinces + territories.
          </p>
          <div className="mt-7 flex flex-wrap items-center gap-3">
            <Link to="/signup" data-testid="cb-hub-cta-signup"
              className="hidden">
              Start building credit <ArrowRight className="w-4 h-4"/>
            </Link>
            <button
              type="button"
              onClick={() => setEnrollOpen(true)}
              data-testid="cb-hub-cta-enroll"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-emerald-700 text-white font-bold text-sm hover:bg-emerald-800 transition-colors"
            >
              Start building credit <ArrowRight className="w-4 h-4"/>
            </button>
            <a href="#how-it-works" data-testid="cb-hub-link-how"
              className="text-sm font-bold text-slate-700 hover:text-emerald-700 inline-flex items-center gap-1.5">
              How it works <ArrowRight className="w-3.5 h-3.5"/>
            </a>
          </div>

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-3xl">
            <Stat icon={TrendingUp} label="Avg. score lift" value="+25-50 pts" sub="first 12 months"/>
            <Stat icon={ShieldCheck} label="Bureaus reached" value="2 of 2" sub="Equifax + TransUnion"/>
            <Stat icon={MapPin} label="Provinces covered" value="13 / 13" sub="all of Canada"/>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how-it-works" className="py-16 md:py-20 bg-white">
        <div className="max-w-5xl mx-auto px-5 md:px-8">
          <div className="text-xs uppercase tracking-[0.22em] font-bold text-emerald-700">How it works</div>
          <h2 className="font-display mt-2 text-2xl sm:text-3xl md:text-4xl font-bold tracking-tighter">
            Four steps. Zero friction.
          </h2>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="cb-hub-steps">
            {[
              { n: 1, t: "Enroll in 60 seconds", d: "Verify your lease + landlord. Free for tenants — landlords pay $0 too." },
              { n: 2, t: "Pay rent the way you already do", d: "e-Transfer, pre-authorized debit, cheque — we don't change your routine." },
              { n: 3, t: "We report monthly", d: "On-time payments turn into a positive tradeline on your credit file at both bureaus." },
              { n: 4, t: "Watch your score climb", d: "Most tenants see a measurable lift within 90 days. Free credit monitoring included." },
            ].map((s) => (
              <div key={s.n} className="p-5 rounded-2xl border border-slate-200 bg-white hover:border-emerald-400 hover:shadow-md transition-all" data-testid={`cb-hub-step-${s.n}`}>
                <div className="w-9 h-9 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center font-display font-bold">{s.n}</div>
                <div className="font-display font-bold text-base text-slate-900 mt-3">{s.t}</div>
                <p className="text-xs text-slate-600 mt-1.5 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Province grid */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-emerald-50/40 via-white to-sky-50/40 border-y border-slate-200">
        <div className="max-w-6xl mx-auto px-5 md:px-8">
          <div className="text-xs uppercase tracking-[0.22em] font-bold text-emerald-700">By province</div>
          <h2 className="font-display mt-2 text-2xl sm:text-3xl md:text-4xl font-bold tracking-tighter">
            Pick your province for the local details
          </h2>
          <p className="mt-3 text-sm md:text-base text-slate-600 max-w-2xl">
            Each province has its own bureau partner, rent norms, and eligibility nuances. Tap your province for a tailored breakdown.
          </p>

          {loading && (
            <div className="mt-8 py-10 text-center text-slate-500 text-sm flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin"/> Loading provinces…
            </div>
          )}

          {!loading && (
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="cb-hub-provinces">
              {provinces.map((p) => (
                <Link key={p.slug} to={p.page_url} data-testid={`cb-hub-province-${p.slug}`}
                  className="group p-5 rounded-2xl border border-slate-200 bg-white hover:border-emerald-400 hover:shadow-md transition-all flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center flex-none font-bold text-sm">
                    {p.code}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider font-bold text-slate-500">
                      <MapPin className="w-3 h-3"/> {p.name}
                    </div>
                    <div className="font-display text-base font-bold text-slate-900 mt-1 leading-snug truncate">
                      Build credit in {p.name}
                    </div>
                    <div className="text-xs text-slate-500 mt-1.5">
                      {p.bureau} · Avg rent ${p.avg_rent.toLocaleString()}
                    </div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-emerald-600 flex-none opacity-70 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all mt-1"/>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-20">
        <div className="max-w-3xl mx-auto px-5 md:px-8">
          <div className="text-xs uppercase tracking-[0.22em] font-bold text-emerald-700">Common questions</div>
          <h2 className="font-display mt-2 text-2xl sm:text-3xl md:text-4xl font-bold tracking-tighter">
            Honest answers.
          </h2>
          <div className="mt-6 space-y-3" data-testid="cb-hub-faqs">
            {FAQS.map((f, i) => (
              <details key={i} className="p-4 rounded-xl border border-slate-200 bg-white group" data-testid={`cb-hub-faq-${i}`}>
                <summary className="font-display font-bold cursor-pointer text-slate-900 flex items-start justify-between gap-3">
                  <span>{f.q}</span>
                  <ArrowRight className="w-4 h-4 text-emerald-600 flex-none mt-1 group-open:rotate-90 transition-transform"/>
                </summary>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="py-16 bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-5 md:px-8 text-center">
          <Building2 className="w-10 h-10 mx-auto text-emerald-400"/>
          <h2 className="font-display mt-4 text-2xl sm:text-3xl md:text-4xl font-bold tracking-tighter">
            Your rent is already building someone&apos;s equity.<br/>
            Make it build your credit too.
          </h2>
          <p className="mt-3 text-sm md:text-base text-slate-300 max-w-2xl mx-auto">
            Sign up in under a minute. We&apos;ll handle the rest.
          </p>
          <button type="button" onClick={() => setEnrollOpen(true)} data-testid="cb-hub-footer-cta"
            className="inline-flex items-center gap-2 mt-6 px-6 py-3 rounded-full bg-emerald-500 text-slate-900 font-bold text-sm hover:bg-emerald-400 transition-colors">
            Start free <ArrowRight className="w-4 h-4"/>
          </button>
        </div>
      </section>
      <EnrollDialog open={enrollOpen} onClose={() => setEnrollOpen(false)} provinces={provinces} defaultProvince={provinces[0]?.slug}/>
    </div>
  );
}

function Stat({ icon: Icon, label, value, sub }) {
  return (
    <div className="p-4 rounded-2xl border border-slate-200 bg-white/80 backdrop-blur" data-testid={`cb-stat-${label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
      <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-700 inline-flex items-center gap-1">
        <Icon className="w-3 h-3"/> {label}
      </div>
      <div className="font-display text-2xl font-bold text-slate-900 mt-1 tabular-nums">{value}</div>
      <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>
    </div>
  );
}

const FAQS = [
  { q: "Is Credit Builder really free for tenants?",
    a: "Yes. Tenants pay $0. Central Rentals reports your rent payments at no cost — it's part of our mission to make Canadian rental housing fairer." },
  { q: "Which credit bureaus receive my rent data?",
    a: "Both Equifax Canada and TransUnion Canada. In Quebec, TransUnion is the primary bureau most lenders pull from, so reporting there is especially valuable." },
  { q: "Will rent reporting hurt my credit score?",
    a: "Only on-time and late payments are reported as positive or neutral history. Missed payments — defined as 30+ days past due — can be reported as negative, but you'll always be notified before that happens." },
  { q: "Does my landlord need to enroll first?",
    a: "Either side can start. If your landlord is already on Central Rentals, you'll see Credit Builder in your tenant dashboard. If not, we'll help you invite them in one click." },
  { q: "How quickly will I see my score change?",
    a: "Most tenants see their first new tradeline appear on their credit file within 30-60 days of enrollment, with a measurable score lift inside 90 days." },
];

// ────────────────────────────────────────────────────────────────────────
// /credit-builder/:province — Province SEO page
// ────────────────────────────────────────────────────────────────────────
export function CreditBuilderProvince() {
  const { province } = useParams();
  const [content, setContent] = useState(null);
  const [error, setError]     = useState("");
  const [loading, setLoading] = useState(true);
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [manifest, setManifest]     = useState([]);

  // Load the province manifest so the modal's province dropdown is
  // populated with the canonical list — not just the current province.
  useEffect(() => {
    fetch(`/api/credit-builder/manifest`)
      .then((r) => r.json())
      .then((d) => setManifest(d.provinces || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    fetch(`/api/credit-builder/${province}/content`)
      .then(async (r) => {
        if (!r.ok) throw new Error(await r.text() || `HTTP ${r.status}`);
        return r.json();
      })
      .then((d) => { if (!cancelled) setContent(d); })
      .catch((e) => { if (!cancelled) setError(String(e?.message || e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [province]);

  const provinceName = content?.province_name || (province || "").replace(/-/g, " ");
  useDocumentMeta({
    title: content?.hero_headline
      ? `${content.hero_headline} | ${provinceName} | Central Rentals`
      : `Credit Builder in ${provinceName} | Central Rentals`,
    description: content?.intro_paragraph || "Build credit by reporting your on-time rent payments to Equifax and TransUnion Canada.",
    canonical: `https://centralrentals.ca/credit-builder/${province}`,
  });

  if (error && !loading) {
    // Bad slug → return to hub
    return <Navigate to="/credit-builder" replace/>;
  }

  const jsonLd = content && {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        name: `Credit Builder — ${content.province_name}`,
        serviceType: "Rent reporting & credit building",
        areaServed: { "@type": "AdministrativeArea", name: content.province_name },
        description: content.intro_paragraph,
        provider: {
          "@type": "Organization",
          name: "Central Rentals Canada",
          url: "https://centralrentals.ca",
        },
      },
      {
        "@type": "FAQPage",
        mainEntity: (content.faqs || []).map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ],
  };

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="credit-builder-province">
      {jsonLd && <JsonLd data={jsonLd}/>}
      <PublicHeader/>

      {/* Hero */}
      <section className="bg-gradient-to-br from-emerald-50 via-white to-sky-50 border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-5 md:px-8 py-14 md:py-20">
          <Link to="/credit-builder" data-testid="cb-province-back"
            className="text-xs text-slate-500 hover:text-emerald-700 inline-flex items-center gap-1 font-bold">
            <ArrowRight className="w-3 h-3 rotate-180"/> All provinces
          </Link>
          {loading ? (
            <div className="mt-6 py-12 text-center text-slate-500 text-sm flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin"/> Loading {provinceName}…
            </div>
          ) : content && (
            <>
              <div className="mt-4 text-[11px] tracking-[0.22em] uppercase font-bold text-emerald-700 inline-flex items-center gap-1.5">
                <MapPin className="w-3 h-3"/> {content.province_name}
              </div>
              <h1 className="font-display mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter leading-[1.05] max-w-3xl"
                  data-testid="cb-province-headline">
                {content.hero_headline}
              </h1>
              <p className="mt-4 text-base md:text-lg text-slate-700 max-w-2xl leading-relaxed"
                 data-testid="cb-province-sub">
                {content.hero_sub}
              </p>
              <div className="mt-7 flex flex-wrap items-center gap-3">
                <button type="button" onClick={() => setEnrollOpen(true)} data-testid="cb-province-cta"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-emerald-700 text-white font-bold text-sm hover:bg-emerald-800 transition-colors">
                  Enroll for free <ArrowRight className="w-4 h-4"/>
                </button>
              </div>
            </>
          )}
        </div>
      </section>

      {content && !loading && (
        <>
          {/* Intro */}
          <section className="py-12 md:py-16 bg-white">
            <div className="max-w-3xl mx-auto px-5 md:px-8 prose prose-slate">
              <p className="text-base md:text-lg text-slate-700 leading-relaxed" data-testid="cb-province-intro">
                {content.intro_paragraph}
              </p>
            </div>
          </section>

          {/* Benefits */}
          <section className="py-12 md:py-16 bg-gradient-to-br from-emerald-50/40 to-white border-y border-slate-200">
            <div className="max-w-5xl mx-auto px-5 md:px-8">
              <div className="text-xs uppercase tracking-[0.22em] font-bold text-emerald-700">Why it works</div>
              <h2 className="font-display mt-2 text-2xl sm:text-3xl font-bold tracking-tighter">
                Benefits for {content.province_name} renters
              </h2>
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="cb-province-benefits">
                {(content.benefits || []).map((b, i) => (
                  <div key={i} className="p-4 rounded-xl border border-slate-200 bg-white">
                    <div className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-600 flex-none mt-0.5"/>
                      <div>
                        <div className="font-display font-bold text-sm text-slate-900">{b.title}</div>
                        <p className="text-xs text-slate-600 mt-1 leading-relaxed">{b.body}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* How it works (steps) */}
          <section className="py-12 md:py-16 bg-white">
            <div className="max-w-4xl mx-auto px-5 md:px-8">
              <div className="text-xs uppercase tracking-[0.22em] font-bold text-emerald-700">How it works</div>
              <h2 className="font-display mt-2 text-2xl sm:text-3xl font-bold tracking-tighter">
                From enrollment to bureau report
              </h2>
              <ol className="mt-6 space-y-3 list-decimal list-inside" data-testid="cb-province-howitworks">
                {(content.how_it_works || []).map((step, i) => (
                  <li key={i} className="text-sm md:text-base text-slate-700 leading-relaxed">
                    {step}
                  </li>
                ))}
              </ol>
            </div>
          </section>

          {/* Province-specific notes + eligibility */}
          <section className="py-12 md:py-16 bg-slate-50 border-y border-slate-200">
            <div className="max-w-4xl mx-auto px-5 md:px-8 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="text-xs uppercase tracking-[0.22em] font-bold text-emerald-700">{content.province_name} specifics</div>
                <h3 className="font-display mt-2 text-xl sm:text-2xl font-bold tracking-tighter">
                  Local details that matter
                </h3>
                <p className="mt-3 text-sm md:text-base text-slate-700 leading-relaxed" data-testid="cb-province-notes">
                  {content.province_notes}
                </p>
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.22em] font-bold text-emerald-700">Eligibility</div>
                <h3 className="font-display mt-2 text-xl sm:text-2xl font-bold tracking-tighter">
                  Who can enroll
                </h3>
                <ul className="mt-3 space-y-1.5 text-sm text-slate-700" data-testid="cb-province-eligibility">
                  {(content.eligibility || []).map((e, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <CheckCircle className="w-3.5 h-3.5 text-emerald-600 flex-none mt-1"/>
                      <span>{e}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>

          {/* FAQs */}
          <section className="py-12 md:py-20">
            <div className="max-w-3xl mx-auto px-5 md:px-8">
              <div className="text-xs uppercase tracking-[0.22em] font-bold text-emerald-700">FAQs — {content.province_name}</div>
              <h2 className="font-display mt-2 text-2xl sm:text-3xl font-bold tracking-tighter">
                Quick answers
              </h2>
              <div className="mt-6 space-y-3" data-testid="cb-province-faqs">
                {(content.faqs || []).map((f, i) => (
                  <details key={i} className="p-4 rounded-xl border border-slate-200 bg-white group" data-testid={`cb-province-faq-${i}`}>
                    <summary className="font-display font-bold cursor-pointer text-slate-900 flex items-start justify-between gap-3">
                      <span>{f.q}</span>
                      <ArrowRight className="w-4 h-4 text-emerald-600 flex-none mt-1 group-open:rotate-90 transition-transform"/>
                    </summary>
                    <p className="mt-2 text-sm text-slate-600 leading-relaxed">{f.a}</p>
                  </details>
                ))}
              </div>
            </div>
          </section>

          {/* Final CTA */}
          <section className="py-14 md:py-20 bg-emerald-700 text-white">
            <div className="max-w-3xl mx-auto px-5 md:px-8 text-center">
              <Mail className="w-9 h-9 mx-auto text-emerald-200"/>
              <h2 className="font-display mt-4 text-2xl sm:text-3xl md:text-4xl font-bold tracking-tighter">
                Ready to start in {content.province_name}?
              </h2>
              <button type="button" onClick={() => setEnrollOpen(true)} data-testid="cb-province-footer-cta"
                className="inline-flex items-center gap-2 mt-6 px-6 py-3 rounded-full bg-white text-emerald-700 font-bold text-sm hover:bg-emerald-50 transition-colors">
                Enroll free <ArrowRight className="w-4 h-4"/>
              </button>
            </div>
          </section>
        </>
      )}
      <EnrollDialog open={enrollOpen} onClose={() => setEnrollOpen(false)} provinces={manifest} defaultProvince={province}/>
    </div>
  );
}
